import {Breakpoints} from '@angular/cdk/layout';
import {Component, Inject} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, NEVER} from 'rxjs';

import {FormManagerService} from '@sim/form';
import {
  BaseTableComponent,
  makeQueryBuilder,
  makeQueryRules,
  TableField,
  TableFilterType,
  TableSelection,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import {CardType, filePath, SourceArcApiService, SwalService} from '@atmi/core';
import {
  SourceArc,
  SourceArcCard,
  SourceArchivial,
  SourceArchivialFilter,
  SourceArchivialFilterMeta,
  SourceArcMeta,
} from '@atmi/core/models';
import {
  AddSourceArchDialogComponent,
  IAddArchDialogData
} from '@atmi/shared/add-source-arch-dialog/add-source-arch-dialog.component';
import {SourceArchivialFiltersComponent} from '../filters/source-archivial-filters/source-archivial-filters.component';

export interface SourceArcDialogData {
  sourceArc?: SourceArcCard;
  order?: number;
  objectId?: string;
  objectType?: CardType;
}

@Component({
  selector: 'app-source-arc-dialog',
  styleUrls: ['./source-arc-dialog.component.scss'],
  templateUrl: './source-arc-dialog.component.html',
})
export class SourceArcDialogComponent
  extends BaseTableComponent<SourceArchivial>
  implements UiTableComponent<SourceArchivial> {

  readonly MobileFiltersComponent = SourceArchivialFiltersComponent;

  readonly fields: TableField<SourceArchivial>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'chekbox',
      selectable: true,
    },
    {
      context: (x) => x.previewUrl ? `${this.fileUrl}${x.previewUrl}` : undefined,
      header: this.translate.instant('Anteprima'),
      key: 'previewUrl',
      template: 'img',
    },
    {
      header: this.translate.instant('Titolo'),
      key: 'titolo',
    },
    {
      header: this.translate.instant('Tipo'),
      key: 'tipo',
    },
    {
      header: this.translate.instant('Codice Unico'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'codiceUnico',
    },
    {
      header: this.translate.instant('Anno Edizione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'annoEdizione',
    },
    {
      header: this.translate.instant('Data creazione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'createdAt',
      sortable: true,
      template: 'date-time',
    },
  ];

  readonly filters$ = new BehaviorSubject<TableFilterType | undefined>(undefined);

  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) =>
      this.sourceArcApi.index({page, limit, sort, filter})
  );

  readonly sourceArcForm: UntypedFormGroup;

  readonly title = this.data.sourceArc
    ? this.translate.instant('Modifica fonte archivistica')
    : this.translate.instant('Aggiungi fonte archivistica');

  readonly mainActionLabel = this.data.sourceArc
    ? this.translate.instant('Sostituisci')
    : this.translate.instant('Collega');

  constructor(
    public dialog: MatDialog,
    private readonly dialogRef: MatDialogRef<SourceArcDialogComponent>,
    private readonly formManager: FormManagerService,
    private readonly sourceArcApi: SourceArcApiService,
    protected readonly translate: TranslateService,
    protected readonly router: Router,
    protected readonly route: ActivatedRoute,
    protected readonly swalService: SwalService,
    @Inject(filePath) private readonly fileUrl: string,
    @Inject(MAT_DIALOG_DATA) public readonly data: SourceArcDialogData,
  ) {
    super(translate, router, route, swalService, undefined, 'local');

    if (this.data.sourceArc && this.data.sourceArc.docArc.id) {
      const sourceArc: SourceArc = {
        ...this.data.sourceArc,
        docArc: this.data.sourceArc.docArc.id,
      };

      this.selectedItems = [this.data.sourceArc.docArc];
      this.sourceArcForm = this.formManager.makeForm(SourceArcMeta, sourceArc);
    } else if (this.data.order) {
      this.sourceArcForm = this.formManager.makeForm(SourceArcMeta, {ordine: this.data.order + 1, docArc: ''});
    } else {
      throw new Error('Cannot set source arc without an order');
    }
  }

  handleCreateAndAdd() {
    const data: IAddArchDialogData = {
      objectId: this.data.objectId || '',
      objectTye: this.data.objectType || 'oa'
    };
    const dialogRef = this.dialog.open(AddSourceArchDialogComponent, {
      width: '70vw',
      height: '80vh',
      data
    });

    dialogRef.afterClosed().subscribe((result: SourceArchivial) => {
      // inviare direttamente al chiamante!!!
      const res = {
        docArc: result.id,
        ordine: (this.data?.order || 0) + 1
      };
      this.dialogRef.close(res);
    });
  }

  completeHandler() {
    if (this.sourceArcForm.valid) {
      this.dialogRef.close(this.sourceArcForm.value);
    } else {
      this.sourceArcForm.markAllAsTouched();
    }
  }

  selectHandler(selectedNews: TableSelection<SourceArchivial>) {
    super.selectHandler(selectedNews);
    if (selectedNews.selected.length > 0) {
      this.sourceArcForm.controls.docArc.setValue(selectedNews.selected[0].id);
    }
  }

  closeHandler() {
    this.dialogRef.close();
  }

  deleteConcreteHandler() {
    return NEVER;
  }

  deleteSelectedConcreteHandler() {
    return NEVER;
  }

  applyFilterWithDelayHandler(filters: SourceArchivialFilter) {
    const rules = makeQueryRules(filters, SourceArchivialFilterMeta);
    const query = makeQueryBuilder(rules);

    this.filters$.next(query);
  }
}
